import clsx from 'clsx';
import { FC, lazy, Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import NotificationCard from 'src/components/common/notification';
import { ERROR_MESSAGE } from 'src/components/errors';
import StudentNav from 'src/components/global-nav/student-nav';
import { BackpackIcon } from 'src/components/icons';
import { useUserAssets } from 'src/hooks/use-user-assets.hook';
import {
  hasElementaryStudentAppsEntitlement,
  hasHubEntitlement,
} from 'src/services/entitlement.service';

import './elementary-school-error.scss';

const ErrorImage = lazy(
  () =>
    import(
      'src/components/elementary-school-view/elementary-school-error/elementary-school-error-image'
    ),
);

interface Props {
  withImage?: boolean;
  errorMessage?: string;
}

const ElementarySchoolError: FC<Props> = ({
  withImage = true,
  errorMessage,
}) => {
  const { t } = useTranslation();
  const [shouldShowBackpackError, setShouldShowBackpackError] =
    useState<boolean>(false);

  const ERROR_TEXT = {
    GENERAL: t('Uh Oh! Something went wrong.'),
    BACKPACK: t("We don't have anything to share here. Check your backpack!"),
  };
  const { data: userAssets } = useUserAssets();

  useEffect(() => {
    if (errorMessage === ERROR_MESSAGE.ACCESS_DENIED) {
      const hasElementaryStudentAppsAsset =
        hasElementaryStudentAppsEntitlement(userAssets);
      const hasHubAsset = hasHubEntitlement(userAssets);

      if (hasElementaryStudentAppsAsset && hasHubAsset) {
        setShouldShowBackpackError(true);
      }
    }
  }, [userAssets, errorMessage]);

  const errorImage = shouldShowBackpackError ? (
    <div>
      <BackpackIcon />
    </div>
  ) : (
    <div className="elementary-school-error__img-container">
      <Suspense fallback={<></>}>
        <ErrorImage />
      </Suspense>
    </div>
  );

  return (
    <>
      <StudentNav />
      <div className="elementary-school-error-container" role="alert">
        <div
          className={clsx('elementary-school-error', {
            'elementary-school-error--with-image': withImage,
            'elementary-school-error--backpack': shouldShowBackpackError,
          })}
        >
          <NotificationCard
            imageComponent={withImage && <>{errorImage}</>}
            title={
              shouldShowBackpackError ? ERROR_TEXT.BACKPACK : ERROR_TEXT.GENERAL
            }
          />
        </div>
      </div>
    </>
  );
};

export default ElementarySchoolError;
